/**
 * The only quantity this application optimises: expected share of the pool,
 * CONDITIONAL on winning. It says nothing about the chance of winning, which is
 * fixed at 1 / totalCombinations(game) per grid and cannot be moved.
 *
 * Expected co-winners on rank 1, given that this combination comes out:
 *
 *     E[co-winners] = volume x popularityIndex(combination) / totalCombinations
 *
 * where popularityIndex is 1.00 for a combination as popular as the average one.
 * A uniformly played crowd would give volume / totalCombinations regardless of
 * the grid; the index is what the popularity model contributes.
 */
import type { GameConfig } from './games';
import { totalCombinations } from './games';
import { comboPopularity, DEFAULT_COEF } from './popularity';
import type { Coefficients } from './popularity';
import type { Grid } from './draw';

export interface SharingEstimate {
  /** Popularity index of the main numbers alone. 1.00 = average. */
  mainIndex: number;
  /** Popularity index of main numbers and bonus numbers combined. */
  fullIndex: number;
  /**
   * fullIndex after the measured elasticity, i.e. the factor that actually applies
   * to the number of co-winners. Always closer to 1 than fullIndex, because the
   * imported coefficients overstate how much French play differs by combination.
   */
  effectiveIndex: number;
  /** Expected number of OTHER winners on rank 1, if this combination comes out. */
  expectedCoWinners: number;
  /** Expected share of the rank-1 pool: 1 / (1 + co-winners). */
  expectedShare: number;
  /** Same, for a grid of average popularity, as a baseline. */
  baselineCoWinners: number;
  baselineShare: number;
  /**
   * How much of the share gain actually reaches the player, after the game's
   * sharing exposure. Near zero for a fixed-prize game such as EuroDreams.
   */
  realisedGain: number;
}

export function estimateSharing(
  grid: Grid,
  g: GameConfig,
  coef: Coefficients = DEFAULT_COEF,
): SharingEstimate {
  const mainIndex = comboPopularity(grid.main, g.n, coef);
  const bonusIndex = grid.bonus.length > 0 ? comboPopularity(grid.bonus, g.bmax, coef) : 1;
  const fullIndex = mainIndex * bonusIndex;

  const total = totalCombinations(g);
  const baselineCoWinners = g.typicalVolume / total;

  // The popularity index is raised to the MEASURED elasticity, not used raw.
  // Regressed on the FDJ payout reports, French play varies with the popularity
  // index far less than the imported coefficients assume: b = 0,56 on the Loto,
  // 0,30 on EuroMillions. Using the index raw here overstated the benefit of a
  // low-popularity grid by a factor of roughly 2,5 to 5. See npm run calibrate.
  const effectiveIndex = Math.pow(fullIndex, g.popularityElasticity);
  const expectedCoWinners = baselineCoWinners * effectiveIndex;

  const expectedShare = 1 / (1 + expectedCoWinners);
  const baselineShare = 1 / (1 + baselineCoWinners);

  // Gross improvement in share, damped by how much of the prize is actually
  // parimutuel. On a fixed-prize game there is nothing to share, so nothing to gain.
  const gross = expectedShare / baselineShare - 1;
  return {
    mainIndex,
    fullIndex,
    effectiveIndex,
    expectedCoWinners,
    expectedShare,
    baselineCoWinners,
    baselineShare,
    realisedGain: gross * g.sharingExposure,
  };
}
